import React, { useMemo, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  createFilterOptions,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  PLAN2_STATUS_OPTIONS,
  TREATMENT2_STATUS_OPTIONS,
  getPreferredPlan2Status,
  isPlan2NormalStatus,
  normalizePlan2Values,
  reconcilePlan2Selection,
} from "./plan2Catalog";

const filterFindings = createFilterOptions();

const filterWithWriteIn = (options, params) => {
  const filtered = filterFindings(options, params);
  const inputValue = String(params.inputValue || "").trim();
  if (
    inputValue &&
    !options.some((option) => option.toLowerCase() === inputValue.toLowerCase()) &&
    !filtered.some((option) => String(option).toLowerCase() === inputValue.toLowerCase())
  ) {
    filtered.push(inputValue);
  }
  return filtered;
};

/**
 * Exam2-style table for diagnostic/treatment category items with status chips.
 */
const Plan2CategoryTable = ({
  items = [],
  values = {},
  pendingFlip = {},
  onChangeItemValue,
  onItemFocus,
  onItemBlur,
  onDocumentNormal,
  documentNormalLabel = "Document normal exam",
  extraOptionsById = {},
  /** "diagnostic" → Ordered, pending + flip; "treatment" → empty default, no flip. */
  variant = "diagnostic",
  /** When false (treatment *2), Tab only advances — does not fill a preferred status. */
  tabFillsPreferred = true,
  tipsOpen = false,
  /** Map of itemId → tip line string(s) or tip objects with .line */
  tipsByItemId = {},
  /** Optional control on the Mark All row (right-aligned with the table). */
  toolbarEnd = null,
}) => {
  const inputRefs = useRef([]);
  const statusOptions =
    variant === "treatment" ? TREATMENT2_STATUS_OPTIONS : PLAN2_STATUS_OPTIONS;

  const formValues = useMemo(
    () => ({
      rows: Object.fromEntries(
        (items || []).map((item) => [item.id, normalizePlan2Values(values[item.id])]),
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      items.map((item) => `${item.id}\0${normalizePlan2Values(values[item.id]).join("\u0001")}`).join("\n"),
    ],
  );

  const { control, getValues, setValue } = useForm({
    values: formValues,
  });

  const focusAt = (index) => {
    if (index < 0) return;
    if (index >= items.length) {
      inputRefs.current[items.length - 1]?.blur?.();
      return;
    }
    const node = inputRefs.current[index];
    if (node) {
      node.focus();
      if (typeof node.select === "function") node.select();
    }
  };

  const acceptPreferredAndAdvance = (item, index) => {
    if (tabFillsPreferred) {
      const current = normalizePlan2Values(getValues(`rows.${item.id}`));
      if (current.length === 0) {
        const preferred = getPreferredPlan2Status(item.id, { pendingFlip, variant });
        const next = [preferred];
        setValue(`rows.${item.id}`, next, { shouldDirty: true });
        onChangeItemValue?.(item.id, next);
      }
    }
    focusAt(index + 1);
  };

  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No items in this section.
      </Typography>
    );
  }

  return (
    <Box>
      {onDocumentNormal || toolbarEnd ? (
        <Box
          sx={{
            display: "flex",
            justifyContent:
              onDocumentNormal && toolbarEnd
                ? "space-between"
                : onDocumentNormal
                  ? "flex-start"
                  : "flex-end",
            alignItems: "flex-start",
            gap: 2,
            mb: 2,
          }}
        >
          {onDocumentNormal ? (
            <Button variant="outlined" size="small" onClick={onDocumentNormal}>
              {documentNormalLabel}
            </Button>
          ) : null}
          {toolbarEnd}
        </Box>
      ) : null}
      <Stack spacing={2}>
        {items.map((item, index) => {
          const extra = Array.isArray(extraOptionsById[item.id]) ? extraOptionsById[item.id] : [];
          const options = [
            ...new Set([
              ...statusOptions,
              ...extra.map((o) => String(o || "").trim()).filter(Boolean),
            ]),
          ];
          const preferred = getPreferredPlan2Status(item.id, { pendingFlip, variant });
          const rawTips = tipsByItemId[item.id];
          const tipLines = Array.isArray(rawTips)
            ? rawTips
                .map((tip) => (typeof tip === "string" ? tip : tip?.line || ""))
                .map((line) => String(line || "").trim())
                .filter(Boolean)
            : rawTips
              ? [typeof rawTips === "string" ? rawTips : rawTips.line].filter(Boolean)
              : [];
          return (
            <Box key={item.id}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
                  gap: 1.5,
                  alignItems: { sm: "center" },
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {item.name}
                </Typography>
                <Controller
                  name={`rows.${item.id}`}
                  control={control}
                  render={({ field }) => {
                    const value = normalizePlan2Values(field.value);
                    return (
                      <Autocomplete
                        multiple
                        freeSolo
                        selectOnFocus
                        clearOnBlur
                        handleHomeEndKeys
                        filterSelectedOptions
                        options={options}
                        filterOptions={filterWithWriteIn}
                        value={value}
                        onChange={(_, next) => {
                          const raw = (Array.isArray(next) ? next : []).map((entry) =>
                            typeof entry === "string" ? entry : String(entry || ""),
                          );
                          const list = reconcilePlan2Selection(raw, field.value);
                          field.onChange(list);
                          onChangeItemValue?.(item.id, list);
                        }}
                        onFocus={() => onItemFocus?.(item.id)}
                        onBlur={() => onItemBlur?.(item.id)}
                        onKeyDown={(event) => {
                          if (
                            event.key !== "Tab" ||
                            event.ctrlKey ||
                            event.metaKey ||
                            event.altKey
                          ) {
                            return;
                          }
                          event.preventDefault();
                          event.stopPropagation();
                          if (event.shiftKey) {
                            focusAt(index - 1);
                            return;
                          }
                          acceptPreferredAndAdvance(item, index);
                        }}
                        renderTags={(tagValue, getTagProps) =>
                          tagValue.map((option, tagIndex) => {
                            const abnormal = !isPlan2NormalStatus(option);
                            const { key, ...tagProps } = getTagProps({ index: tagIndex });
                            return (
                              <Chip
                                {...tagProps}
                                key={key}
                                label={option}
                                size="small"
                                color={abnormal ? "error" : "default"}
                                variant={abnormal ? "outlined" : "filled"}
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                          placeholder={
                            value.length
                              ? "Add another status"
                              : tabFillsPreferred
                                ? `Tab for “${preferred}”`
                                : "Add another status"
                          }
                            fullWidth
                            inputRef={(node) => {
                              inputRefs.current[index] = node;
                              if (typeof params.inputRef === "function") params.inputRef(node);
                              else if (params.inputRef) params.inputRef.current = node;
                            }}
                          />
                        )}
                      />
                    );
                  }}
                />
              </Box>
              <Collapse in={tipsOpen && tipLines.length > 0} unmountOnExit>
                <Stack spacing={0.5} sx={{ mt: 1, pl: { sm: "216px" } }}>
                  {tipLines.map((line) => (
                    <Typography key={line} variant="body2" color="text.secondary">
                      {line}
                    </Typography>
                  ))}
                </Stack>
              </Collapse>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

/** List | Table toggle — same control pattern as Exam | Exam 2. */
export const Plan2ModeToggle = ({ listLabel, tableLabel, mode, onModeChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={mode === "table" ? "table" : "list"}
    onChange={(_, value) => {
      if (!value || !onModeChange) return;
      onModeChange(value);
    }}
    sx={{ mb: 1.5, display: "flex", flexWrap: "wrap" }}
  >
    <ToggleButton value="list" aria-label={listLabel}>
      {listLabel}
    </ToggleButton>
    <ToggleButton value="table" aria-label={tableLabel}>
      {tableLabel}
    </ToggleButton>
  </ToggleButtonGroup>
);

export default Plan2CategoryTable;
